import AddRoadIcon from '@mui/icons-material/AddRoad';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';

import { HeaderCard } from '../../../event/components/HeaderCard';
import { Layout } from '../../../navigation/components/Layout/Layout';
import { CarpoolTripData } from '../../graphql/query/carpoolTripsQuery.graphql';
import { CarpoolTripDrawer } from './components/CarpoolTripDrawer/CarpoolTripDrawer';
import { CreateCarpoolTripDrawer } from './components/CreateCarpoolTripDrawer/CreateCarpoolTripDrawer';
import { useCarpool } from './useCarpool';

const formatUserName = (
  firstname: string | null,
  lastname: string | null,
  nickname?: string | null,
) => {
  const fullName = `${firstname ?? ''} ${lastname ?? ''}`.trim();

  if (!nickname) {
    return fullName || 'Participant';
  }

  return fullName ? `${fullName} (${nickname})` : nickname;
};

const getTripChip = (
  trip: CarpoolTripData,
  currentUserId: string | null,
): { color: 'primary' | 'success'; label: string } | null => {
  if (trip.driver.id === currentUserId) {
    return { color: 'primary', label: 'Vous conduisez' };
  }

  if (trip.passengers.some((passenger) => passenger.id === currentUserId)) {
    return { color: 'success', label: 'Vous êtes passager' };
  }

  return null;
};

export const Carpool = () => {
  const {
    canCreateTrip,
    currentEvent,
    currentUser,
    isCreateTripDrawerOpen,
    isStaff,
    joinedTrip,
    onCloseCreateTripDrawer,
    onCloseTrip,
    onOpenCreateTripDrawer,
    onOpenTrip,
    selectedTrip,
    trips,
  } = useCarpool();
  const canBrowseCarpool = currentEvent?.hasPaidTicket || isStaff;

  if (!currentEvent) {
    return (
      <Layout activeTab="carpool" title="Covoiturage">
        <Box />
      </Layout>
    );
  }

  return (
    <Layout activeTab="carpool" title="Covoiturage">
      <Stack spacing={3}>
        <Box>
          <HeaderCard
            title="Organisez votre trajet"
            description={`Proposez une voiture ou rejoignez un trajet déjà publié pour le ${currentEvent.name}.`}
          />
          {canCreateTrip ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                startIcon={<AddRoadIcon />}
                variant="contained"
                onClick={onOpenCreateTripDrawer}
              >
                Proposer un trajet
              </Button>
            </Box>
          ) : null}
        </Box>

        {!canBrowseCarpool ? (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {"Le covoiturage s'ouvre après le paiement"}
              </Typography>
              <Typography color="text.secondary">
                Payez votre place pour proposer un trajet ou rejoindre une
                voiture.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Votre statut
                    </Typography>
                    <Typography variant="h6">
                      {joinedTrip
                        ? joinedTrip.driver.id === currentUser?.id
                          ? 'Vous proposez déjà un trajet'
                          : 'Vous avez déjà rejoint un trajet'
                        : 'Aucun trajet choisi pour le moment'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {joinedTrip
                        ? `${joinedTrip.pickupTown} • ${format(new Date(joinedTrip.pickupAt), "d MMMM 'à' HH:mm", { locale: fr })}`
                        : 'Le covoiturage reste optionnel.'}
                    </Typography>
                  </Box>
                  {joinedTrip ? (
                    <Button
                      variant="outlined"
                      onClick={() => onOpenTrip(joinedTrip.id)}
                    >
                      Voir mon trajet
                    </Button>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {trips.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {trips.map((trip) => {
                  const statusChip = getTripChip(trip, currentUser?.id ?? null);
                  const isFull = trip.remainingSeats === 0;

                  return (
                    <Card
                      key={trip.id}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isFull ? 'grey.300' : 'divider',
                        bgcolor: isFull ? 'grey.100' : 'background.paper',
                        color: isFull ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                          >
                            <Box>
                              <Typography variant="h5">
                                {trip.pickupTown}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {format(
                                  new Date(trip.pickupAt),
                                  "EEEE d MMMM 'à' HH:mm",
                                  { locale: fr },
                                )}
                              </Typography>
                            </Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {statusChip ? (
                                <Chip
                                  color={statusChip.color}
                                  label={statusChip.label}
                                />
                              ) : null}
                              <Chip
                                label={
                                  isFull
                                    ? 'Complet'
                                    : `${trip.remainingSeats}/${trip.seats} restante${trip.seats > 1 ? 's' : ''}`
                                }
                                color={isFull ? 'default' : undefined}
                                variant={isFull ? 'filled' : 'outlined'}
                              />
                            </Stack>
                          </Stack>

                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <PersonOutlineOutlinedIcon
                                fontSize="small"
                                color={isFull ? 'disabled' : 'action'}
                              />
                              <Typography variant="body2">
                                {formatUserName(
                                  trip.driver.firstname,
                                  trip.driver.lastname,
                                  trip.driver.nickname,
                                )}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {`${trip.driver.studyTown} • ${trip.driver.studies}`}
                            </Typography>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Diversity3OutlinedIcon
                              fontSize="small"
                              color={isFull ? 'disabled' : 'action'}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {trip.passengers.length} passager
                              {trip.passengers.length > 1 ? 's' : ''} déjà
                              inscrit
                            </Typography>
                          </Stack>

                          {trip.description ? (
                            <Typography
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                              }}
                              variant="body2"
                            >
                              {trip.description}
                            </Typography>
                          ) : null}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Button
                          endIcon={<RouteOutlinedIcon />}
                          onClick={() => onOpenTrip(trip.id)}
                          color={isFull ? 'inherit' : 'primary'}
                          variant="outlined"
                        >
                          Voir le trajet
                        </Button>
                      </CardActions>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Aucun trajet publié pour le moment
                  </Typography>
                  <Typography color="text.secondary">
                    Soyez le premier à proposer une voiture pour cet événement.
                  </Typography>
                </CardContent>
                {canCreateTrip ? (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={onOpenCreateTripDrawer}
                    >
                      Proposer un trajet
                    </Button>
                  </CardActions>
                ) : null}
              </Card>
            )}
          </>
        )}
      </Stack>

      {selectedTrip ? (
        <CarpoolTripDrawer
          currentUserId={currentUser?.id ?? null}
          eventId={currentEvent.id}
          hasPaidTicket={currentEvent.hasPaidTicket}
          isOpen
          isStaff={isStaff}
          onClose={onCloseTrip}
          trip={selectedTrip}
          userJoinedTripId={joinedTrip?.id ?? null}
        />
      ) : null}

      <CreateCarpoolTripDrawer
        eventId={currentEvent.id}
        eventStartAt={new Date(currentEvent.startAt)}
        isOpen={isCreateTripDrawerOpen}
        onClose={onCloseCreateTripDrawer}
      />
    </Layout>
  );
};
